/**
 * BooksFilterSheet Component (v3)
 * 
 * Multi-country location system with searchable Country → City selectors,
 * "Clear All" button, and filter count indicator.
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Check, Trash2, Search, MapPin } from 'lucide-react';
import {
  Sheet,
  SheetContent,
} from '../ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Input } from '../ui/input';
import { useStudentHub } from '../../contexts/StudentHubContext';

export const DEFAULT_FILTERS = {
  mode: '',
  priceMin: undefined,
  priceMax: undefined,
  country: '',
  city: '',
  sort: 'newest',
};

export function countActiveFilters(filters) {
  if (!filters) return 0;
  let count = 0;
  if (filters.mode && filters.mode !== '' && filters.mode !== 'all') count++;
  if (filters.priceMin !== undefined && filters.priceMin !== null) count++;
  if (filters.priceMax !== undefined && filters.priceMax !== null) count++;
  if (filters.country && filters.country !== '') count++;
  if (filters.city && filters.city !== '') count++;
  if (filters.sort && filters.sort !== 'newest') count++;
  return count;
}

const BOOK_MODES = [
  { key: 'all', label_en: 'All', label_ar: 'الكل' },
  { key: 'free', label_en: 'Free', label_ar: 'مجاني' },
  { key: 'sell', label_en: 'Buy', label_ar: 'شراء' },
  { key: 'rent', label_en: 'Rent', label_ar: 'إيجار' },
  { key: 'download', label_en: 'Download', label_ar: 'تحميل' },
];

const PRICE_CHIPS = [
  { key: 'free', label_en: 'Free only', label_ar: 'مجاني فقط', min: 0, max: 0 },
  { key: 'under5k', label_en: 'Under 5,000', label_ar: 'أقل من 5,000', min: 0, max: 5000 },
  { key: '5k-20k', label_en: '5,000–20,000', label_ar: '5,000–20,000', min: 5000, max: 20000 },
  { key: 'over20k', label_en: '20,000+', label_ar: '20,000+', min: 20000, max: null },
];

const SORT_OPTIONS = [
  { key: 'newest', label_en: 'Newest', label_ar: 'الأحدث' },
  { key: 'popular', label_en: 'Popular', label_ar: 'الأكثر شعبية' },
  { key: 'price_asc', label_en: 'Lowest Price', label_ar: 'الأقل سعراً' },
  { key: 'price_desc', label_en: 'Highest Price', label_ar: 'الأعلى سعراً' },
];

export default function BooksFilterSheet({
  open,
  onOpenChange,
  filters,
  onFiltersChange,
  onApply,
}) {
  const { t, i18n } = useTranslation();
  const { academicLevel, updateAcademicLevel, ACADEMIC_LEVELS, getLevelLabel } = useStudentHub();
  
  const isArabic = i18n.language?.startsWith('ar');
  const isRTL = document.dir === 'rtl' || document.documentElement.dir === 'rtl' || isArabic;
  
  const [draft, setDraft] = useState({ ...DEFAULT_FILTERS });
  const [draftLevel, setDraftLevel] = useState('all');
  const [countries, setCountries] = useState([]);

  // Fetch countries on mount
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/student-hub/countries`)
      .then(r => r.ok ? r.json() : [])
      .then(setCountries)
      .catch(() => {});
  }, []);

  // Sync draft from applied filters on open
  useEffect(() => {
    if (open) {
      setDraft({ ...filters });
      setDraftLevel(academicLevel || 'all');
    }
  }, [open, filters, academicLevel]);

  const draftActiveCount = useMemo(() => countActiveFilters(draft), [draft]);
  const priceDisabled = draft.mode === 'free';

  // Get cities for selected country
  const citiesForCountry = useMemo(() => {
    if (!draft.country) return [];
    const c = countries.find(co => co.key === draft.country);
    return c?.cities || [];
  }, [draft.country, countries]);

  const handlePriceChip = useCallback((chip) => {
    if (priceDisabled && chip.key !== 'free') return;
    if (chip.key === 'free') {
      setDraft(prev => ({ ...prev, mode: 'free', priceMin: 0, priceMax: 0 }));
    } else {
      setDraft(prev => ({ ...prev, priceMin: chip.min, priceMax: chip.max }));
    }
  }, [priceDisabled]);

  const isPriceChipActive = useCallback((chip) => {
    if (chip.key === 'free') return draft.mode === 'free' || (draft.priceMin === 0 && draft.priceMax === 0);
    return draft.priceMin === chip.min && draft.priceMax === chip.max;
  }, [draft]);

  const handleClearAll = useCallback(() => {
    setDraft({ ...DEFAULT_FILTERS });
    setDraftLevel('all');
  }, []);

  const handleApply = useCallback(async () => {
    if (draftLevel !== academicLevel && draftLevel !== 'all') {
      await updateAcademicLevel(draftLevel);
    } else if (draftLevel === 'all' && academicLevel) {
      await updateAcademicLevel('');
    }
    onFiltersChange({ ...draft });
    onApply?.();
    onOpenChange(false);
  }, [draftLevel, academicLevel, draft, updateAcademicLevel, onFiltersChange, onApply, onOpenChange]);

  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="bottom" 
        className="books-filter-sheet"
        hideCloseButton={true}
        ariaLabel={t('home.filters')}
      >
        {/* Header */}
        <div className="filter-sheet-header" data-testid="filter-sheet-header">
          <button className="filter-header-close" onClick={handleClose} aria-label={t('rolesele.close')} data-testid="filter-close-btn">
            <X size={20} />
          </button>
          <div className="filter-header-center">
            <h2 className="filter-header-title">{t('home.filters')}</h2>
            {draftActiveCount > 0 && (
              <span className="filter-header-badge" data-testid="filter-count-badge">{draftActiveCount}</span>
            )}
          </div>
          <div style={{ width: 36 }} />
        </div>

        {/* Body */}
        <div className="filter-sheet-body">
          {/* 1. Academic Level */}
          <div className="filter-section">
            <label className="filter-label">
              {t('onborole.academicLevel')}
              <span className="filter-label-hint">{t('bfs.affectsAllSections')}</span>
            </label>
            <Select value={draftLevel} onValueChange={setDraftLevel}>
              <SelectTrigger className="filter-select">
                <SelectValue>{getLevelLabel(draftLevel, isRTL)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('bfs.allLevels')}</SelectItem>
                {ACADEMIC_LEVELS.map(l => (
                  <SelectItem key={l.key} value={l.key}>{isRTL ? l.label_ar : l.label_en}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 2. Type */}
          <div className="filter-section">
            <label className="filter-label">{t('bfs.type')}</label>
            <div className="filter-chips">
              {BOOK_MODES.map(m => (
                <button
                  key={m.key}
                  className={`filter-chip ${draft.mode === m.key || (m.key === 'all' && !draft.mode) ? 'active' : ''}`}
                  onClick={() => setDraft(prev => ({ ...prev, mode: m.key === 'all' ? '' : m.key, ...(m.key === 'free' ? { priceMin: 0, priceMax: 0 } : {}) }))}
                >
                  {isRTL ? m.label_ar : m.label_en}
                </button>
              ))}
            </div>
          </div>

          {/* 3. Sort */}
          <div className="filter-section">
            <label className="filter-label">{t('slp.sortBy')}</label>
            <div className="filter-chips">
              {SORT_OPTIONS.map(s => (
                <button
                  key={s.key}
                  className={`filter-chip ${(draft.sort || 'newest') === s.key ? 'active' : ''}`}
                  onClick={() => setDraft(prev => ({ ...prev, sort: s.key }))}
                >
                  {isRTL ? s.label_ar : s.label_en}
                </button>
              ))}
            </div>
          </div>

          {/* 4. Price */}
          <div className={`filter-section ${priceDisabled ? 'disabled' : ''}`}>
            <label className="filter-label">{t('bfs.priceSdg')}</label>
            <div className="filter-chips">
              {PRICE_CHIPS.map(chip => (
                <button
                  key={chip.key}
                  className={`filter-chip ${isPriceChipActive(chip) ? 'active' : ''}`}
                  onClick={() => handlePriceChip(chip)}
                  disabled={priceDisabled && chip.key !== 'free'}
                >
                  {isRTL ? chip.label_ar : chip.label_en}
                </button>
              ))}
            </div>
            <div className="filter-price-inputs">
              <Input
                type="number"
                placeholder={t('home.min')}
                value={draft.priceMin ?? ''}
                onChange={(e) => setDraft(prev => ({ ...prev, priceMin: e.target.value ? parseInt(e.target.value) : undefined }))}
                disabled={priceDisabled}
                className="filter-price-input"
              />
              <span className="filter-price-sep">–</span>
              <Input
                type="number"
                placeholder={t('home.max')}
                value={draft.priceMax ?? ''}
                onChange={(e) => setDraft(prev => ({ ...prev, priceMax: e.target.value ? parseInt(e.target.value) : undefined }))}
                disabled={priceDisabled}
                className="filter-price-input"
              />
            </div>
          </div>

          {/* 5. Location — Country + City */}
          <div className="filter-section">
            <label className="filter-label">
              <MapPin size={14} />
              {t('bfs.location')}
            </label>

            <SearchableSelect
              label={t('bfs.country')}
              placeholder={t('bfs.searchCountry')}
              items={countries}
              value={draft.country}
              isRTL={isRTL}
              onChange={(v) => setDraft(prev => ({ ...prev, country: v, city: '' }))}
              testId="filter-country"
            />

            <SearchableSelect
              label={t('bfs.city')}
              placeholder={t('bfs.searchCity')}
              items={citiesForCountry}
              value={draft.city}
              isRTL={isRTL}
              onChange={(v) => setDraft(prev => ({ ...prev, city: v }))}
              disabled={!draft.country}
              testId="filter-city"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="filter-sheet-footer" data-testid="filter-sheet-footer">
          <button
            className={`filter-clear-btn ${draftActiveCount === 0 ? 'disabled' : ''}`}
            onClick={handleClearAll}
            disabled={draftActiveCount === 0}
            data-testid="filter-clear-all-btn"
          >
            <Trash2 size={16} />
            {t('bfs.clearAll')}
          </button>

          <button className="filter-apply-btn" onClick={handleApply} data-testid="filter-apply-btn">
            <Check size={18} />
            {t('bfs.applyFilters')}
            {draftActiveCount > 0 && (
              <span className="filter-apply-count">{draftActiveCount}</span>
            )}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}


/**
 * SearchableSelect — inline searchable dropdown for filter sheet
 */
function SearchableSelect({ label, placeholder, items, value, onChange, isRTL, disabled, testId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter(i =>
      (i.name_en || '').toLowerCase().includes(q) ||
      (i.name_ar || '').toLowerCase().includes(q)
    );
  }, [items, query]);

  const selectedLabel = useMemo(() => {
    const item = items.find(i => i.key === value);
    if (!item) return '';
    return isRTL ? item.name_ar : item.name_en;
  }, [items, value, isRTL]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Focus input on open
  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  const handleSelect = (key) => {
    onChange(key === value ? '' : key);
    setIsOpen(false);
    setQuery('');
  };

  return (
    <div className={`searchable-select ${disabled ? 'searchable-disabled' : ''}`} ref={wrapRef} data-testid={testId}>
      <button
        type="button"
        className={`searchable-trigger ${value ? 'has-value' : ''}`}
        onClick={() => { if (!disabled) { setIsOpen(!isOpen); setQuery(''); } }}
        disabled={disabled}
        data-testid={`${testId}-trigger`}
      >
        <span className="searchable-trigger-text">
          {value ? selectedLabel : label}
        </span>
        <Search size={14} className="searchable-trigger-icon" />
      </button>

      {isOpen && (
        <div className="searchable-dropdown" data-testid={`${testId}-dropdown`}>
          <div className="searchable-search-row">
            <Search size={14} className="searchable-search-icon" />
            <input
              ref={inputRef}
              type="text"
              className="searchable-search-input"
              placeholder={placeholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="searchable-list">
            {filtered.length === 0 ? (
              <div className="searchable-empty">—</div>
            ) : (
              filtered.map(item => (
                <button
                  key={item.key}
                  type="button"
                  className={`searchable-item ${item.key === value ? 'active' : ''}`}
                  onClick={() => handleSelect(item.key)}
                  data-testid={`${testId}-item-${item.key}`}
                >
                  {isRTL ? item.name_ar : item.name_en}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
